import { useCallback, useEffect, useState } from 'react';
import { useRelayEnvironment, useRefetchableFragment, fetchQuery, graphql } from 'react-relay';

import { getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers';

import { type usePersistedWalletAddress_viewer$key } from './__generated__/usePersistedWalletAddress_viewer.graphql';
import usePersistedWalletAddressQuery, {
    type usePersistedWalletAddressQuery as usePersistedWalletAddressQueryType,
    type usePersistedWalletAddressQuery$variables,
} from './__generated__/usePersistedWalletAddressQuery.graphql';

type AuthResponse = {
    user: {
        serviceId: string;
        ethereumWalletAddress: {
            address: string;
        };
    };
};

type Props = {
    viewer: usePersistedWalletAddress_viewer$key | null | undefined;
};

export function usePersistedWalletAddress({ viewer: viewerRef }: Props): {
    persistedAddress: string;
    userId: string;
} {
    const environment = useRelayEnvironment();
    const [viewer, refetch] = useRefetchableFragment<
        usePersistedWalletAddressQueryType,
        usePersistedWalletAddress_viewer$key
    >(
        graphql`
            fragment usePersistedWalletAddress_viewer on Viewer
            @argumentDefinitions(fetchUser: { type: "Boolean!" }, userId: { type: "String!" })
            @refetchable(queryName: "usePersistedWalletAddressQuery") {
                user(userId: $userId) @include(if: $fetchUser) {
                    serviceId
                    ethereumWalletAddress {
                        address
                    }
                }
            }
        `,
        viewerRef
    );

    const refetchUserWithoutSuspending = useCallback(
        (variables: usePersistedWalletAddressQuery$variables) => {
            return fetchQuery(environment, usePersistedWalletAddressQuery, variables).subscribe({
                complete: () => {
                    refetch(variables, { fetchPolicy: 'store-only' });
                },
            });
        },
        [environment, refetch]
    );

    const user = viewer?.user;

    const [userId, setUserId] = useState(user?.serviceId ?? '');
    const [persistedAddress, setPersistedAddress] = useState(
        user?.ethereumWalletAddress?.address?.toLowerCase() ?? ''
    );

    useEffect(() => {
        const { ethereumWalletAddress, serviceId: buyerId } = user || {};
        const userAddress = ethereumWalletAddress?.address?.toLowerCase();
        if (buyerId) {
            setUserId(buyerId);
        }
        if (userAddress) {
            setPersistedAddress(userAddress);
        }
    }, [user]);

    useEffect(() => {
        const buyerId = getBuyerId(document.cookie);
        if (buyerId) {
            refetchUserWithoutSuspending({ userId: buyerId, fetchUser: true });
        }
        addLoginHandlers((authResponse: AuthResponse) => {
            const authPersistedAddress =
                authResponse?.user?.ethereumWalletAddress?.address?.toLowerCase();
            const authUserId = authResponse?.user?.serviceId;

            setPersistedAddress(authPersistedAddress);
            setUserId(authUserId);
            refetchUserWithoutSuspending({ userId: authUserId, fetchUser: true });
        });
    }, [refetch, refetchUserWithoutSuspending]);

    return {
        persistedAddress,
        userId,
    };
}
